/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useState } from "react";
import HeaderSection from "../../../common/headerSection";
import PageHeader from "../../../common/pageHeader";
import { Link, useNavigate, useParams } from "react-router-dom";
import { RootState } from "../../../../store/store";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { LuActivity, LuAlertOctagon, LuArrowRight, LuBarChart3, LuBuilding2, LuCalendarClock, LuCalendarDays, LuCalendarRange, LuCircleDot, LuClock, LuCloudSun, LuEye, LuFileStack, LuFileWarning, LuFlaskConical, LuHandMetal, LuHardDrive, LuHistory, LuLayers, LuPackage, LuTrendingDown, LuUserSquare, LuUserSquare2, LuWaves } from "react-icons/lu";
import { BiCalendarWeek, BiMapPin, BiWind } from "react-icons/bi";

const SubNavigationComponent = () => {
  const navigate = useNavigate();
  // const areaId = useSelector((state: RootState) => state.graphType.GraphType);
  const [selectedTab, setSelectedTab] = useState("2");
  const { t } = useTranslation();

  const { areaId } = useParams();

  let pageDescription = "";

  switch (areaId) {
    case "Excursion":
      pageDescription = t("pageDescription.excursionDashboard.description");
      break;
    case "Trends":
      pageDescription = t("pageDescription.trendsDashboard.description");
      break;
    case "Violations":
      pageDescription = t("pageDescription.violationsDashboard.description");
      break;
    case "Summary":
      pageDescription = t("pageDescription.summaryDashboard.description");
      break;
    default:
      pageDescription = "";
  }


  const handleTabClick = (tabName : any) => {
    setSelectedTab(tabName);
  };

  const options = [
    {
      label: t("common.by_area"),
      description: t("analyticalDashboard.about_by_area"),
      defaultValue:'area',
      icon: <LuBuilding2 />,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600'
    },
    {
      label: t("common.by_location"),
      description: t("analyticalDashboard.about_by_location"),
      defaultValue:'location',
      icon: <BiMapPin />,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600'
    },
    {
      label: t("common.by_operator"),
      description: t("analyticalDashboard.about_by_operator"),
      defaultValue:'operator',
      icon: <LuUserSquare />,
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-600'
    },
    {
      label: t("common.daily"),
      description: t("analyticalDashboard.about_daily"),
      defaultValue:'day',
      icon: <LuCalendarDays />,
      iconBg: 'bg-orange-50',
      iconColor: 'text-orange-600'
    },
    
    {
      label: t("common.weekly"),
      description: t("analyticalDashboard.about_weekly"),
      defaultValue:'week',
      icon: <LuBarChart3 />,
      iconBg: 'bg-cyan-50',
      iconColor: 'text-cyan-600'
    },
    {
      label: t("common.monthly"),
      description: t("analyticalDashboard.about_monthly"),
      defaultValue:'month',
      icon: <LuCalendarRange />,
      iconBg: 'bg-rose-50',
      iconColor: 'text-rose-600'
    },
    {
      label: t("common.quarterly"),
      description: t("analyticalDashboard.about_quarterly"),
      defaultValue:'quarter',
      icon: <BiCalendarWeek />,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600'
    },
    {
      label: t("common.yearly"),
      description: t("analyticalDashboard.about_yearly"),
      defaultValue:'year',
      icon: <LuHistory />,
      iconBg: 'bg-indigo-50',
      iconColor: 'text-indigo-600'
    },
  ];

  const optionsEscursion = [
    {
      label: t("common.daily"),
      defaultValue:'day',
      description: 'Real-time overview of limit violations and out-of-spec incidents for the current 24-hour cycle.',
      icon: <LuClock />,
      iconBg: 'bg-rose-50',
      iconColor: 'text-rose-600'
    },
    {
      label: t("common.weekly"),
      defaultValue:'week',
      description: 'Weekly aggregate of excursion events to identify short-term quality deviations and patterns.',
      icon: <LuTrendingDown />,
      iconBg: 'bg-orange-50',
      iconColor: 'text-orange-600'
    },
    {
      label: t("common.monthly"),
      defaultValue:'month',
      description: 'Consolidated monthly analysis for facility performance monitoring and recurring risk identification.',
      icon: <LuCalendarRange />,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600'
    },
    {
      label: t("common.quarterly"),
      defaultValue:'quarter',
      description: 'Detailed quarterly summaries for regulatory reporting and continuous process improvement logs.',
      icon: <LuCalendarDays />,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600'
    },
    {
      label: t("common.yearly"),
      defaultValue:'year',
      description: 'Annual comprehensive risk analytics to support long-term site qualification and quality strategy.',
      icon: <LuHistory />,
      iconBg: 'bg-indigo-50',
      iconColor: 'text-indigo-600'
    }
  ];

  const optionsSummary = [
    {
      label: t("common.by_area"),
      defaultValue: "area",
      description: "Comprehensive operational summaries categorized by controlled production zones.",
      icon: <LuBuilding2 />,
      iconBg: "bg-blue-50",
      iconColor: "text-blue-600",
    },
    {
      label: t("common.by_location"),
      defaultValue: "location",
      description: "Site-wide activity logs filtered by specific environmental monitoring points.",
      icon: <BiMapPin />,
      iconBg: "bg-emerald-50",
      iconColor: "text-emerald-600",
    },
    {
      label: t("common.by_operator"),
      defaultValue: "operator",
      description: "Activity and performance reports tracked per individual technician or operator.",
      icon: <LuUserSquare />,
      iconBg: "bg-purple-50",
      iconColor: "text-purple-600",
    },
    {
      label: t("dashboard.schedule_summary"),
      defaultValue: "schedule",
      description: "Consolidated overview of all completed, pending, and overdue monitoring tasks.",
      icon: <LuCalendarClock />,
      iconBg: "bg-orange-50",
      iconColor: "text-orange-600",
    },
    {
      label: t("dashboard.exception_summary"),
      defaultValue: "exception",
      description: "Detailed report of all non-conformance incidents and monitoring exceptions.",
      icon: <LuAlertOctagon />,
      iconBg: "bg-rose-50",
      iconColor: "text-rose-600",
    },
    {
      label: t("dashboard.plate_sampling"),
      defaultValue: "plate_sampling",
      description: "Detailed metrics for all microbial media plate sampling activities.",
      icon: <LuLayers />,
      iconBg: "bg-indigo-50",
      iconColor: "text-indigo-600",
    },
    {
      label: t("dashboard.active_sampling"),
      defaultValue: "active_sampling",
      description: "Volumetric air monitoring results and total microbial counts per cubic meter.",
      icon: <BiWind />,
      iconBg: "bg-cyan-50",
      iconColor: "text-cyan-600",
    },
    {
      label: t("dashboard.passive_sampling"),
      defaultValue: "passive_sampling",
      description: "Settle plate exposure metrics and resulting microbial fallout data.",
      icon: <LuCloudSun />,
      iconBg: "bg-sky-50",
      iconColor: "text-sky-600",
    },
    {
      label: t("dashboard.monitoring_report"),
      defaultValue: "monitoring_report",
      description: "Documentation of gowning effectiveness and fingertip sampling results.",
      icon: <LuUserSquare2 />,
      iconBg: "bg-violet-50",
      iconColor: "text-violet-600",
    },
    {
      label: t("dashboard.surface_monitoring"),
      defaultValue: "surface_monitoring",
      description: "Contamination levels detected on critical surfaces and equipment contact points.",
      icon: <LuActivity />,
      iconBg: "bg-teal-50",
      iconColor: "text-teal-600",
    },
    {
      label: t("dashboard.glove_port_report"),
      defaultValue: "glove_port_report",
      description: "Barrier system integrity and isolator glove monitoring oversight.",
      icon: <LuHandMetal />,
      iconBg: "bg-slate-50",
      iconColor: "text-slate-600",
    },
    {
      label: t("dashboard.surface_monitoring_report"),
      defaultValue: "surface_monitoring_report",
      description: "Holistic data collection from surface contact plate sampling protocols.",
      icon: <LuCircleDot />,
      iconBg: "bg-pink-50",
      iconColor: "text-pink-600",
    },
    {
      label: t("dashboard.anaerobic_monitoring"),
      defaultValue: "anaerobic_monitoring",
      description: "Atmospheric control and microbial results from anaerobic environment tests.",
      icon: <LuFlaskConical />,
      iconBg: "bg-green-50",
      iconColor: "text-green-600",
    },
    {
      label: t("dashboard.nitrogen_monitoring"),
      defaultValue: "nitrogen_monitoring",
      description: "Purity and microbial status reports for compressed nitrogen utility lines.",
      icon: <LuWaves />,
      iconBg: "bg-blue-100",
      iconColor: "text-blue-700",
    },
    {
      label: t("dashboard.compressed_air_monitoring"),
      defaultValue: "compressed_air_monitoring",
      description: "Analytical oversight of compressed air line contamination levels.",
      icon: <BiWind />,
      iconBg: "bg-slate-100",
      iconColor: "text-slate-700",
    },
    {
      label: t("dashboard.configuration_report"),
      defaultValue: "configuration_report",
      description: "Status and historical monitoring data for specific cleanroom equipment.",
      icon: <LuHardDrive />,
      iconBg: "bg-zinc-50",
      iconColor: "text-zinc-600",
    },
    {
      label: t("dashboard.inspection"),
      defaultValue: "inspection",
      description: "Summary of all periodic facility and equipment visual assessment logs.",
      icon: <LuEye />,
      iconBg: "bg-amber-100",
      iconColor: "text-amber-700",
    },
    {
      label: t("dashboard.incident_deviation"),
      defaultValue: "incident_deviation",
      description: "Formal record of all process deviations and subsequent investigations.",
      icon: <LuFileWarning />,
      iconBg: "bg-red-50",
      iconColor: "text-red-600",
    },
    {
      label: t("dashboard.plate_report"),
      defaultValue: "plate_report",
      description: "Comprehensive tracking of plate usage, incubation progress, and disposal.",
      icon: <LuFileStack />,
      iconBg: "bg-fuchsia-50",
      iconColor: "text-fuchsia-600",
    },
    {
      label: t("dashboard.production_batch_summary_report"),
      defaultValue: "production_batch_summary_report",
      description: "Consolidated monitoring data tied specifically to finished product batch IDs.",
      icon: <LuPackage />,
      iconBg: "bg-yellow-50",
      iconColor: "text-yellow-700",
    },
  ];

  const optionsViolation = [
    {
      label: t("common.by_area"),
      defaultValue: "area",
      description: "Compliance violations categorized by specific controlled production sectors.",
      icon: <LuBuilding2 />,
      iconBg: "bg-red-50",
      iconColor: "text-red-500",
    },
    {
      label: t("common.by_location"),
      defaultValue: "location",
      description: "Identification of recurring compliance breaches at specific monitoring points.",
      icon: <BiMapPin />,
      iconBg: "bg-orange-50",
      iconColor: "text-orange-500",
    },
    {
      label: t("common.by_operator"),
      defaultValue: "operator",
      description: "Analytical tracking of personnel participation in aseptic practice violations.",
      icon: <LuUserSquare />,
      iconBg: "bg-amber-50",
      iconColor: "text-amber-500",
    },
    {
      label: t("common.daily"),
      defaultValue: "day",
      description: "Snapshot of regulatory and internal protocol deviations recorded in the last 24 hours.",
      icon: <LuCalendarDays />,
      iconBg: "bg-rose-50",
      iconColor: "text-rose-500",
    },
    {
      label: t("common.weekly"),
      defaultValue: "week",
      description: "Weekly aggregate of compliance failures for process performance qualification.",
      icon: <LuBarChart3 />,
      iconBg: "bg-slate-50",
      iconColor: "text-slate-500",
    },
    {
      label: t("common.monthly"),
      defaultValue: "month",
      description: "Comprehensive monthly evaluation of site-wide regulatory alignment status.",
      icon: <LuCalendarRange />,
      iconBg: "bg-purple-50",
      iconColor: "text-purple-500",
    },
    {
      label: t("common.quarterly"),
      defaultValue: "quarter",
      description: "Strategic datasets used for formal quarterly quality management performance reviews.",
      icon: <LuCalendarDays />,
      iconBg: "bg-blue-50",
      iconColor: "text-blue-500",
    },
    {
      label: t("common.yearly"),
      defaultValue: "year",
      description: "Annual landscape assessment of total compliance performance and major trend shifts.",
      icon: <LuHistory />,
      iconBg: "bg-indigo-50",
      iconColor: "text-indigo-500",
    },
  ];

  interface Option {
    label: string;
    description: string;
    defaultValue: string;
    icon?: React.ReactNode;
    iconBg?: string;
    iconColor?: string;
  }
  
  const renderOptions = () => {
    let optionsToRender: Option[] = []; 
    if (areaId === "Trends") {
      optionsToRender = options;
    } else if (areaId === "Excursion") {
      optionsToRender = optionsEscursion;
    } else if (areaId === "Summary") {
      optionsToRender = optionsSummary;
    } else if (areaId === "Violations") {
      optionsToRender = optionsViolation;
    } else if (areaId === ``) {
      navigate("/analytics-dashboard");
    } 
  
    return optionsToRender.map((option: Option, index: number) => (
//       <div key={index} className="col-6 mb-3">
//   <button
//     className="optionSelect w-100 h-100"
//     onClick={() => handleOptionChange(option.defaultValue.split(' | ')[0])}
//     title={option.defaultValue.split(' | ')[1]}
//     aria-label={`Select ${option.label}: ${option.description}`}
//   >
//     <div className="option-content p-2 p-md-3">
//       <span className="option-label d-block fs-6 fs-md-5">{option.label}</span>
//       <span className="option-description d-block fs-7 fs-md-6 text-muted mt-1">
//         {option.description}
//       </span>
//     </div>
//   </button>
// </div>

      <div
        className="bg-white rounded-[2.5rem] border border-slate-100 p-8 shadow-sm hover:shadow-xl hover:shadow-slate-200/30 transition-all group flex flex-col items-center text-center relative overflow-hidden"
        key={index}
      >
        {/* Decorative background element */}
        <div className={`absolute -right-4 -top-4 w-24 h-24 rounded-full opacity-[0.03] ${option.iconBg}`} />

        <div className={`w-16 h-16 rounded-[1.75rem] ${option.iconBg} ${option.iconColor} flex items-center justify-center mb-6 shadow-sm`}>
          {React.cloneElement(option.icon as React.ReactElement, { className: 'w-8 h-8' })}
        </div>

        <h3 className="text-xl font-black text-[#0F172A] tracking-tight mb-2 group-hover:text-orange-600 transition-colors">{option.label}</h3>
        <p className="text-sm font-medium text-slate-400 mb-8 leading-relaxed px-1">
          {option.description}
        </p>

        <button 
          className="mt-auto flex items-center gap-2 px-6 py-2.5 bg-slate-50 text-[#0F172A] font-black rounded-xl group-hover:bg-orange-500 group-hover:text-white transition-all text-xs uppercase tracking-widest shadow-sm"
          onClick={() => handleOptionChange(option.defaultValue.split(' | ')[0])}
          title={option.defaultValue.split(' | ')[1]}
          aria-label={`Select ${option.label}: ${option.description}`}
        >
          View Report
          <LuArrowRight className="w-4 h-4" />
        </button>
      </div>

    ));
  };
  

  const handleOptionChange = (bulletId: string) => {
    if (areaId == "Trends") {
      navigate(`/trend-analytics/${bulletId}`);
    }
    if (areaId == "Excursion") {
      navigate(`/excursion-report/${bulletId}`);
    }
    if (areaId == "Summary") {
      navigate(`/summary-report/${bulletId}`);
    }
    // if (areaId == "Violations") {
    //   navigate(`/violation-home/${bulletId}/${selectedTab}`);
    // }

    if (areaId === "Violations") {
      let path = `/violation-home/${bulletId}`;
      
      if (selectedTab === "1") {
        path += '_schedule';
      } else if (selectedTab === "2") {
        path += '_exception';
      }
    
      navigate(`${path}/${selectedTab}`);
    }
  };

  return (
    <div>
      <HeaderSection />
       <PageHeader pageHeader={`${areaId} Dashboard`} pageDescription={pageDescription} />
      <nav aria-label="breadcrumb" className="breadcrumb-container row">
        <div className="col-10 d-flex">
          <ol className="breadcrumb">
            <li className="breadcrumb-item dashboard-hover">
              <Link to="/analytics-dashboard">{t('dashboard.analyticalDashboard')}</Link>
            </li>
            <li
              className="breadcrumb-item active selectedArea"
              aria-current="page"
            >
              {areaId} {t('dashboard.title')}
            </li>
          </ol>
        </div>
      </nav>
     {areaId === "Violations" && (
      <div className="violation-tabs">
        {/* <div className={`violation-tab ${selectedTab === "1" ? "active" : ""}`} onClick={() => handleTabClick("1")} style={{"marginLeft" : "10%"}}>
          <div className="tab-content">
            <div className="tab-name">{t('analyticalDashboard.schedule_violations')}</div>
            <div className="tab-description">{t('analyticalDashboard.about_schedule_violations')}</div>
          </div>
        </div> */}
        {/* <div className={`violation-tab ${selectedTab === "2" ? "active" : ""}`} onClick={() => handleTabClick("2")} style={{"marginLeft" : "30%", "marginTop" : "15px"}}>
          <div className="tab-content">
            <div className="tab-name">{t('analyticalDashboard.exception_violation')}</div>
            <div className="tab-description">{t('analyticalDashboard.about_exception_violation')}</div>
          </div>
        </div> */}
        {/* <div className={`violation-tab ${selectedTab === "3" ? "active" : ""}`} onClick={() => handleTabClick("3")}>
          <div className="tab-content">
            <div className="tab-name">{t('analyticalDashboard.discrepancy_violations')}</div>
            <div className="tab-description">{t('analyticalDashboard.about_discrepancy_violations')}</div>
          </div>
        </div> */}
      </div>
    )}
      {/* <div className="col-12 col-md-8 d-flex justify-content-center mt-5">
        <div className="row">{renderOptions()}</div>
      </div> */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 pe-4 md:pe-6 lg:pe-8">
        {renderOptions()}
      </div>
    </div>
  );
};

export default SubNavigationComponent;
